import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "../../Components/LandingPage/Nav/Nav";
import Footer from "../../Components/LandingPage/Footer/Footer";
import { FaShoppingCart, FaTrash, FaRegCalendarAlt, FaShieldAlt } from "react-icons/fa";
import { HiDocumentText } from "react-icons/hi";
import { RotatingLines } from "react-loader-spinner";
import {
    getCarts,
    deleteCartItem,
    deleteCart,
    getStates,
    createMcaDocumentsAndDataRazorpayOrder,
    handleMcaDocumentsAndDataPaymentSuccess,
    getUserInvoiceForm,
} from "../../Services/APIrequests";
import { SuccessSwal, warningSwal } from "../../Util/Toast";
import useStore from "../../Store";
import styles from "./CartPage.module.css";

const CartPage = () => {
    const navigate = useNavigate();
    const { setCartCount } = useStore();
    const isLoggedIn = Boolean(sessionStorage.getItem("token"));

    const [cart, setCart] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [deletingItemId, setDeletingItemId] = useState(null);
    const [clearingCart, setClearingCart] = useState(false);
    const [showClearConfirm, setShowClearConfirm] = useState(false);

    // Billing modal
    const [showBillingModal, setShowBillingModal] = useState(false);
    const [statesList, setStatesList] = useState([]);
    const [billingDetails, setBillingDetails] = useState({
        fullName: "",
        email: "",
        mobileNumber: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        pincode: "",
        GSTIN: "",
        invoiceType: "B2C",
    });

    const gstPercentage = 18;
    const subtotal = cart?.amount || 0;
    const gstAmount = subtotal * (gstPercentage / 100);
    const roundedTotal = parseFloat((subtotal + gstAmount).toFixed(2));

    useEffect(() => {
        const name = sessionStorage.getItem("fullName") || "";
        const email = sessionStorage.getItem("email") || "";
        setBillingDetails(prev => ({ ...prev, fullName: name, email }));

        const fetchStates = async () => {
            try {
                const res = await getStates();
                setStatesList(res.data.states || []);
            } catch (_) {}
        };
        fetchStates();
    }, []);

    const fetchCart = async () => {
        setIsLoading(true);
        try {
            const res = await getCarts();
            if (res.data?.Status === 1) {
                setCart(res.data);
                setCartCount(res.data.docCartItemCount || 0);
            } else {
                setCart(null);
            }
        } catch (_) {
            setCart(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isLoggedIn) fetchCart();
        else setIsLoading(false);
    }, [isLoggedIn]);

    const handleDeleteItem = async (docCartItemId, companyName) => {
        setDeletingItemId(docCartItemId);
        try {
            const res = await deleteCartItem(docCartItemId);
            if (res.status === 200) {
                await SuccessSwal("Removed", `${companyName} removed from cart.`);
                fetchCart();
            } else {
                warningSwal("Error", res.data?.message || "Failed to remove item.");
            }
        } catch (error) {
            warningSwal("Error", error?.response?.data?.message || "Failed to remove item.");
        } finally {
            setDeletingItemId(null);
        }
    };

    const handleClearCart = async () => {
        if (!cart?.docCartId) return;
        setShowClearConfirm(false);
        setClearingCart(true);
        try {
            const res = await deleteCart(cart.docCartId);
            if (res.status === 200) {
                await SuccessSwal("Cart Cleared", "All items removed from cart.");
                setCart(null);
                setCartCount(0);
            } else {
                warningSwal("Error", res.data?.message || "Failed to clear cart.");
            }
        } catch (error) {
            warningSwal("Error", error?.response?.data?.message || "Failed to clear cart.");
        } finally {
            setClearingCart(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setBillingDetails(prev => ({ ...prev, [name]: value }));
    };

    const handleOpenBillingModal = async () => {
        try {
            const invoiceRes = await getUserInvoiceForm().catch(() => null);
            if (invoiceRes?.data?.Status === 1 && invoiceRes.data.data) {
                const d = invoiceRes.data.data;
                setBillingDetails({
                    fullName: d.fullName || "",
                    email: d.email || "",
                    mobileNumber: d.mobileNumber || "",
                    address1: d.address1 || "",
                    address2: d.address2 || "",
                    city: d.city || "",
                    state: d.state || "",
                    pincode: d.pincode || "",
                    GSTIN: d.GSTIN || "",
                    invoiceType: d.invoiceType || "B2C",
                });
            }
        } catch (_) {}
        setShowBillingModal(true);
    };

    const handleFinalPayment = async () => {
        const { fullName, email, mobileNumber, address1, city, state, pincode } = billingDetails;
        if (!fullName || !email || !mobileNumber || !address1 || !city || !state || !pincode) {
            warningSwal("Incomplete Details", "Please fill in all required fields.");
            return;
        }
        try {
            const orderResponse = await createMcaDocumentsAndDataRazorpayOrder({
                docCartId: cart.docCartId,
                docCartItems: items,
                amount: subtotal,
                gstAmount: parseFloat(gstAmount.toFixed(2)),
                totalAmount: roundedTotal,
            });

            if (orderResponse.status === 200 || orderResponse.status === 201) {
                const razorpay_orderId = orderResponse.data.data.razorpay_orderId;

                // Snapshot all data needed for success handler before Razorpay opens
                const successPayload = {
                    fullName: billingDetails.fullName,
                    email: billingDetails.email,
                    mobileNumber: billingDetails.mobileNumber,
                    address1: billingDetails.address1,
                    address2: billingDetails.address2,
                    city: billingDetails.city,
                    state: billingDetails.state,
                    pincode: billingDetails.pincode,
                    GSTIN: billingDetails.GSTIN,
                    invoiceType: billingDetails.invoiceType,
                    docCartId: cart.docCartId,
                    subtotal,
                    gstPercentage,
                    gstAmount: parseFloat(gstAmount.toFixed(2)),
                    totalamount: roundedTotal,
                    razorpay_order_id: razorpay_orderId,
                };

                const options = {
                    key: process.env.REACT_APP_RAZORPAYKEY,
                    amount: Math.round(roundedTotal * 100),
                    currency: "INR",
                    name: "Kohere",
                    description: `MCA Documents for ${items.length} ${items.length === 1 ? "company" : "companies"}`,
                    order_id: razorpay_orderId,
                    handler: async (razorpayResponse) => {
                        try {
                            const body = {
                                ...successPayload,
                                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                                razorpay_signature: razorpayResponse.razorpay_signature,
                            };
                            const response = await handleMcaDocumentsAndDataPaymentSuccess(body);
                            if (response.status === 200) {
                                await SuccessSwal("Success!", response.data.message || "Payment successful! Documents are being prepared.");
                                setCart(null);
                                setCartCount(0);
                                navigate("/mca-documents");
                            } else {
                                warningSwal("Payment Failed", response.data?.message || "Payment verification failed.");
                            }
                        } catch (err) {
                            console.error("Payment success error:", err);
                            warningSwal("Error", "An error occurred after payment. Please contact support.");
                        }
                    },
                    prefill: {
                        name: billingDetails.fullName,
                        email: billingDetails.email,
                        contact: billingDetails.mobileNumber,
                    },
                    theme: { color: "#2563eb" },
                };

                setShowBillingModal(false);
                const rzp = new window.Razorpay(options);
                rzp.open();
            } else {
                warningSwal("Payment Error", "Failed to create order. Please try again.");
            }
        } catch (error) {
            console.error("Order creation error:", error);
            warningSwal("Payment Error", "Failed to initiate payment. Please try again.");
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleString("en-GB", {
            day: "numeric", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit", hour12: true,
        });
    };

    const items = cart?.docCartItems || [];

    return (
        <>
            <Nav />
            <div className={styles.pageWrapper}>
                <div className={styles.container}>
                    <div className={styles.header}>
                        <div className={styles.headerIcon}>
                            <FaShoppingCart size={22} color="#3b82f6" />
                        </div>
                        <div>
                            <h1 className={styles.title}>My Cart</h1>
                            <p className={styles.subtitle}>Review and manage your selected MCA documents</p>
                        </div>
                    </div>

                    {!isLoggedIn ? (
                        <div className={styles.loginRequiredWrapper}>
                            <div className={styles.loginRequiredContent}>
                                <FaShieldAlt size={48} color="#3b82f6" />
                                <h2>Login Required</h2>
                                <p>Please login to view your cart.</p>
                                <button className={styles.loginRedirectBtn} onClick={() => navigate("/login", { state: { from: "/mca-cart" } })}>
                                    Login Now
                                </button>
                            </div>
                        </div>
                    ) : isLoading ? (
                        <div className={styles.loaderContainer}>
                            <RotatingLines strokeColor="#3b82f6" strokeWidth="5" animationDuration="0.75" width="60" visible={true} />
                            <p>Loading cart...</p>
                        </div>
                    ) : items.length === 0 ? (
                        <div className={styles.emptyCart}>
                            <FaShoppingCart size={56} color="#cbd5e1" />
                            <h3>Your cart is empty</h3>
                            <p>Search and add company documents to your cart.</p>
                            <button className={styles.browseBtn} onClick={() => navigate("/mca-documents")}>
                                Browse Companies
                            </button>
                        </div>
                    ) : (
                        <div className={styles.cartLayout}>
                            <div className={styles.itemsSection}>
                                <div className={styles.itemsHeader}>
                                    <h3>{items.length} {items.length === 1 ? "item" : "items"} in cart</h3>
                                    <button
                                        className={styles.clearCartBtn}
                                        onClick={() => setShowClearConfirm(true)}
                                        disabled={clearingCart}
                                    >
                                        {clearingCart ? "Clearing..." : "Clear Cart"}
                                    </button>
                                </div>

                                {items.map((item) => (
                                    <div key={item.docCartItemId} className={styles.cartItem}>
                                        <div className={styles.itemLeft}>
                                            <div className={styles.itemIconBg}>
                                                <HiDocumentText size={20} color="#3b82f6" />
                                            </div>
                                            <div className={styles.itemInfo}>
                                                <h4 className={styles.itemCompany}>{item.CompanyName || "N/A"}</h4>
                                                <span className={styles.itemCin}>{item.CIN}</span>
                                                <div className={styles.itemMeta}>
                                                    <FaRegCalendarAlt size={12} color="#94a3b8" />
                                                    <span>Added: {formatDate(item.createdAt)}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={styles.itemRight}>
                                            <span className={styles.itemPrice}>₹{item.price}</span>
                                            <button
                                                className={styles.deleteItemBtn}
                                                onClick={() => handleDeleteItem(item.docCartItemId, item.CompanyName)}
                                                disabled={deletingItemId === item.docCartItemId}
                                                title="Remove from cart"
                                            >
                                                {deletingItemId === item.docCartItemId ? (
                                                    <RotatingLines strokeColor="#ef4444" strokeWidth="5" animationDuration="0.75" width="16" visible={true} />
                                                ) : (
                                                    <FaTrash size={14} />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className={styles.summarySection}>
                                <h3 className={styles.summaryTitle}>Order Summary</h3>
                                <div className={styles.summaryRow}>
                                    <span>Items ({items.length})</span>
                                    <span>₹{subtotal.toLocaleString("en-IN")}</span>
                                </div>
                                <div className={styles.summaryRow}>
                                    <span>GST ({gstPercentage}%)</span>
                                    <span>₹{Math.round(gstAmount).toLocaleString("en-IN")}</span>
                                </div>
                                <div className={`${styles.summaryRow} ${styles.totalRow}`}>
                                    <span>Total</span>
                                    <span>₹{Math.round(roundedTotal).toLocaleString("en-IN")}</span>
                                </div>
                                <button
                                    className={styles.proceedBtn}
                                    onClick={handleOpenBillingModal}
                                >
                                    Proceed to Pay ₹{Math.round(roundedTotal).toLocaleString("en-IN")}
                                </button>
                                <p className={styles.secureNote}>
                                    <FaShieldAlt size={12} /> Secure payment • Invoice will be emailed
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Footer />

            {/* Billing Modal */}
            {showBillingModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <div>
                                <h2>Billing Details</h2>
                                <p>Enter your billing address for invoice generation</p>
                            </div>
                            <button className={styles.closeBtn} onClick={() => setShowBillingModal(false)}>&times;</button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label>Full Name *</label>
                                    <input type="text" name="fullName" value={billingDetails.fullName} onChange={handleInputChange} placeholder="John Doe" />
                                </div>
                                <div className={styles.formGroup}>
                                    <label>Email *</label>
                                    <input type="email" name="email" value={billingDetails.email} onChange={handleInputChange} placeholder="you@example.com" />
                                </div>
                            </div>
                            <div className={styles.formGroup}>
                                <label>Phone *</label>
                                <input type="text" name="mobileNumber" value={billingDetails.mobileNumber} onChange={handleInputChange} placeholder="+91 98765 43210" />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Address Line 1 *</label>
                                <input type="text" name="address1" value={billingDetails.address1} onChange={handleInputChange} placeholder="Building / Street" />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Address Line 2</label>
                                <input type="text" name="address2" value={billingDetails.address2} onChange={handleInputChange} placeholder="Landmark (optional)" />
                            </div>
                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label>City *</label>
                                    <input type="text" name="city" value={billingDetails.city} onChange={handleInputChange} placeholder="Mumbai" />
                                </div>
                                <div className={styles.formGroup}>
                                    <label>State *</label>
                                    <select name="state" value={billingDetails.state} onChange={handleInputChange} className={styles.stateSelect}>
                                        <option value="">Select State</option>
                                        {statesList.map((st) => (
                                            <option key={st.stateId} value={st.stateName}>{st.stateName}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.formGroup}>
                                    <label>Pincode *</label>
                                    <input type="text" name="pincode" value={billingDetails.pincode} onChange={handleInputChange} placeholder="400001" />
                                </div>
                            </div>
                            <div className={styles.gstSection}>
                                <label>GSTIN (Optional)</label>
                                <input type="text" name="GSTIN" value={billingDetails.GSTIN} onChange={handleInputChange} placeholder="22AAAAA0000A1Z5" />
                                <p className={styles.gstNote}>Enter your GST number for a GST-compliant invoice</p>
                            </div>
                            <div className={styles.billingSummary}>
                                <div className={styles.summaryLine}>
                                    <span>Items</span>
                                    <span>{items.length} {items.length === 1 ? "company" : "companies"}</span>
                                </div>
                                <div className={styles.summaryLine}>
                                    <span>Subtotal</span>
                                    <span>₹{subtotal.toLocaleString("en-IN")}</span>
                                </div>
                                <div className={styles.summaryLine}>
                                    <span>GST ({gstPercentage}%)</span>
                                    <span>₹{Math.round(gstAmount).toLocaleString("en-IN")}</span>
                                </div>
                                <div className={`${styles.summaryLine} ${styles.finalTotal}`}>
                                    <span>Total</span>
                                    <span className={styles.totalVal}>₹{Math.round(roundedTotal).toLocaleString("en-IN")}</span>
                                </div>
                            </div>
                            <button className={styles.modalProceedBtn} onClick={handleFinalPayment}>
                                Proceed to Pay ₹{Math.round(roundedTotal).toLocaleString("en-IN")}
                            </button>
                            <p className={styles.secureNote}>
                                <FaShieldAlt size={12} /> Secure payment • Invoice will be emailed
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Clear cart confirmation modal */}
            {showClearConfirm && (
                <div className={styles.confirmOverlay}>
                    <div className={styles.confirmModal}>
                        <h3 className={styles.confirmTitle}>Clear Cart</h3>
                        <p className={styles.confirmText}>Are you sure you want to remove all items from your cart? This action cannot be undone.</p>
                        <div className={styles.confirmActions}>
                            <button className={styles.confirmCancelBtn} onClick={() => setShowClearConfirm(false)}>
                                Cancel
                            </button>
                            <button className={styles.confirmDeleteBtn} onClick={handleClearCart}>
                                Yes, Clear Cart
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CartPage;
